// src/config/templateThemes.js
//
// A template's formatting: what "the app copies that formatting into the new
// manuscript" means. It is a manuscript theme (src/config/manuscriptThemes.js)
// — the one mechanism the app has for the look of a whole book — chosen by the
// template's `format`, and set to that format's conventions as far as a theme
// can express them: the typeface and size, line spacing, margins, paragraph
// indent and how chapters and scene breaks look.
//
// What a theme cannot express is per-line roles — a screenplay's character cue
// at 3.7", its dialogue at 2.5", an MLA heading block, footnotes. Those need the
// editor to know kinds of line, which is its own piece of work; until then a
// screenplay template gets Courier on its 1.5" binding margin and plain lines.
//
// Built on BASE_THEME like the presets, so anything not named here is the
// prototype's default. Every value is the author's book's, not the app's chrome,
// so colours carry `design-tokens-allow`.
import { BASE_THEME, mergeTheme } from './manuscriptThemes';
import { DEFAULT_BOOK_SIZE, bookSize } from './bookSizes';

// design-tokens-allow — a printed manuscript is black on white
const INK = { text: '#000000', accent: '#000000', paper: '#ffffff' };

// The submission conventions: 1" margins, the body in one face at 12 pt, a plain
// title centred a third of the way down the chapter's first page, no ornaments.
const plainChapters = (font, { sink = 33, upper = false } = {}) => ({
  recto: false, label: 'none', lFont: font, lSize: 12, lCase: 'none', lTrack: 0,
  tFont: font, tSize: 12, tWeight: 400, tItalic: false, tCase: upper ? 'upper' : 'none',
  align: 'center', sink, orn: 'none', first: 'none',
});

const FORMAT_THEMES = {
  // Standard manuscript: Times 12, double-spaced, 0.5" first-line indent.
  standard: {
    name: 'Standard Manuscript',
    page: { top: 1, bottom: 1, inside: 1, outside: 1 },
    body: { font: 'Times New Roman', size: 12, lead: 2, align: 'left', style: 'indent', indent: 3, hyph: false },
    ch: plainChapters('Times New Roman'),
    h: { font: 'Times New Roman', size: 12, weight: 700, italic: false, case: 'none', align: 'left' },
    brk: { style: 'hash', after: 'none' },
    q: { italic: false, indent: 3, size: 100, rule: false },
    folio: { pos: 'top-outside', style: 'plain', opener: false },
    col: INK,
  },
  // Poems: single-spaced, a blank line between stanzas, a 1.5" binding gutter.
  poetry: {
    name: 'Collection Manuscript',
    page: { top: 1, bottom: 1, inside: 1.5, outside: 1 },
    body: { font: 'Times New Roman', size: 12, lead: 1.2, align: 'left', style: 'spaced', indent: 0.5, hyph: false },
    ch: plainChapters('Times New Roman'),
    h: { font: 'Times New Roman', size: 12, weight: 400, italic: false, case: 'none', align: 'left' },
    brk: { style: 'hash', after: 'none' },
    folio: { pos: 'top-outside', style: 'plain', opener: false },
    col: INK,
  },
  // Scripts set in Courier: single-spaced, a blank line between elements, 1.5"
  // on the binding side and 1" elsewhere, act headings in capitals.
  script: {
    name: 'Script Format',
    page: { top: 1, bottom: 1, inside: 1.5, outside: 1 },
    body: { font: 'Courier Prime', size: 12, lead: 1.1, align: 'left', style: 'spaced', indent: 0.5, hyph: false },
    ch: plainChapters('Courier Prime', { sink: 0, upper: true }),
    h: { font: 'Courier Prime', size: 12, weight: 400, italic: false, case: 'upper', align: 'left' },
    brk: { style: 'hash', after: 'none' },
    q: { italic: false, indent: 2, size: 100, rule: false },
    folio: { pos: 'top-outside', style: 'plain', opener: false },
    col: INK,
  },
  // Stage plays: Times 12, single-spaced, a blank line between speeches.
  stage: {
    name: 'Modern Play Format',
    page: { top: 1, bottom: 1, inside: 1, outside: 1 },
    body: { font: 'Times New Roman', size: 12, lead: 1.15, align: 'left', style: 'spaced', indent: 0.5, hyph: false },
    ch: plainChapters('Times New Roman', { sink: 0, upper: true }),
    h: { font: 'Times New Roman', size: 12, weight: 400, italic: false, case: 'upper', align: 'center' },
    brk: { style: 'hash', after: 'none' },
    folio: { pos: 'top-outside', style: 'plain', opener: false },
    col: INK,
  },
  // Planning sheets: a clean working document rather than a submission.
  sheet: {
    name: 'Planning Sheet',
    page: { top: 0.75, bottom: 0.75, inside: 0.75, outside: 0.75 },
    body: { font: 'Inter', size: 10.5, lead: 1.45, align: 'left', style: 'spaced', indent: 0.5, hyph: false },
    ch: {
      recto: false, label: 'none', lFont: 'Inter', lSize: 10, lCase: 'upper', lTrack: 0.1,
      tFont: 'Inter', tSize: 16, tWeight: 700, tItalic: false, tCase: 'none', align: 'left', sink: 0, orn: 'rule', first: 'none',
    },
    h: { font: 'Inter', size: 11, weight: 600, italic: false, case: 'none', align: 'left' },
    brk: { style: 'rule', after: 'none' },
    folio: { pos: 'bottom-outside', style: 'plain', opener: true },
    col: INK,
  },
};

// Which formatting each catalogue `format` gets.
const FORMAT_OF = {
  prose: 'standard',
  'academic-mla': 'standard',
  'academic-apa': 'standard',
  'academic-chicago': 'standard',
  poetry: 'poetry',
  screenplay: 'script',
  comic: 'script',
  stageplay: 'stage',
  sheet: 'sheet',
};

// **Page size.** The conventions above are for a US Letter manuscript, which is
// what a template's book is unless its author picked a size. On a printed
// book's trim they would leave almost nothing: 1" and 1.5" margins on a 4.25"
// pocket book is a column under two inches wide, double-spaced. So below
// Letter the page gets a book's margins and the text a book's spacing, scaled
// to the trim; the typeface, size and everything else stay the format's.
function fitToTrim(over, trimSize) {
  const { widthIn } = bookSize(trimSize || DEFAULT_BOOK_SIZE);
  if (widthIn >= 8.5) return over;
  const small = widthIn < 5;
  const page = small
    ? { top: 0.5, bottom: 0.55, inside: 0.6, outside: 0.4 }
    : { top: 0.7, bottom: 0.75, inside: 0.8, outside: 0.55 };
  const body = { ...over.body, lead: Math.min(over.body.lead, 1.4) };
  if (small) body.size = Math.min(body.size, 11);
  return { ...over, page, body };
}

/**
 * A template's formatting, as the theme copy a draft stores
 * (`draft.manuscriptTheme`): the full theme values plus `name` and `sourceId`.
 * Null for a format with no formatting of its own.
 */
export function templateTheme(template, trimSize = DEFAULT_BOOK_SIZE) {
  const key = FORMAT_OF[template?.format];
  if (!key) return null;
  const { name, ...over } = FORMAT_THEMES[key];
  return { ...mergeTheme(BASE_THEME, fitToTrim(over, trimSize)), name, sourceId: `template-${key}` };
}
